import { VITE_BACKEND_BASE_URL, VITE_LOGGING } from "@/core/env/envVars";

const SENSITIVE_PATTERNS = [
  /password/i,
  /secret/i,
  /token/i,
  /authorization/i,
  /cookie/i,
  /credit.?card|card.?number/i,
  /account.?number|numero.?compte/i,
  /iban/i,
  /ssn|nin|numero.?identification/i,
];

function sanitizeForLog(value: unknown): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value === "string") {
    return SENSITIVE_PATTERNS.some((p) => p.test(value))
      ? "[REDACTED]"
      : value;
  }
  if (Array.isArray(value)) return value.map(sanitizeForLog);
  if (typeof value === "object") {
    const entries = Object.entries(value).map(([k, v]) =>
      SENSITIVE_PATTERNS.some((p) => p.test(k))
        ? ([k, "[REDACTED]"] as [string, unknown])
        : ([k, sanitizeForLog(v)] as [string, unknown])
    );
    return Object.fromEntries(entries);
  }
  return value;
}

function generateCorrelationId(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let id = "";
  for (let i = 0; i < 8; i++) {
    id += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return id;
}

export interface ErrorLogContext {
  route?: string;
  componentStack?: string;
  userId?: string;
  sessionId?: string;
}

export interface ErrorLogResult {
  correlationId: string;
}

/**
 * Centralized frontend error logger. Captures errors with correlation IDs
 * for support reference. Never logs sensitive data.
 */
export function logClientError(
  error: Error,
  context?: ErrorLogContext
): ErrorLogResult {
  const correlationId = generateCorrelationId();

  const sanitizedContext =
    sanitizeForLog(context ?? {}) as Record<string, unknown>;
  const payload = {
    correlationId,
    timestamp: new Date().toISOString(),
    message: error.message,
    stack: error.stack,
    name: error.name,
    ...(typeof sanitizedContext === "object" && sanitizedContext != null
      ? sanitizedContext
      : {}),
  };

  if (VITE_LOGGING === "true") {
    console.error("[ClientError]", payload);
  }

  if (typeof VITE_BACKEND_BASE_URL === "string" && VITE_BACKEND_BASE_URL) {
    const url = `${VITE_BACKEND_BASE_URL.replace(/\/$/, "")}/client-errors`;
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      // Fire-and-forget; avoid noisy console in production
    });
  }

  return { correlationId };
}
